import type { SelectHiredPeopleProps } from './SelectHiredPeople.types';
import { TextField } from 'components/TextField';
import map from 'lodash/map';
import { Checkbox } from 'components/Checkbox';
import { Avatar } from 'components/Avatar';
import { UserGraphic } from 'graphics';
import { ChangeEvent, useMemo, useState } from 'react';
import deburr from 'lodash/deburr';
import differenceBy from 'lodash/differenceBy';
import sortBy from 'lodash/sortBy';
import { Skeleton } from 'components/Skeleton';
import uniqBy from 'lodash/uniqBy';

export const SelectHiredPeople = (props: SelectHiredPeopleProps) => {
  const {
    applicants,
    loading = false,
    onSearchChange,
    selectedApplicants,
    onSelectionChange,
    nonApplicantName,
    onNonApplicantNameChange,
  } = props;

  const applicantsPrimitive = map(applicants, 'id').join(',');
  const selectedApplicantsPrimitive = map(selectedApplicants, 'id').join(',');

  const [showOtherTextField, setShowOtherTextField] = useState(false);

  const sortedApplicants = useMemo(
    () =>
      sortBy(differenceBy(uniqBy(applicants, 'id'), selectedApplicants, 'id'), (applicant) =>
        deburr(applicant.fullName()),
      ),
    [applicantsPrimitive, selectedApplicantsPrimitive],
  );

  const sortedSelectedApplicants = useMemo(
    () => sortBy(uniqBy(selectedApplicants, 'id'), (applicant) => deburr(applicant.fullName())),
    [selectedApplicantsPrimitive],
  );

  const handleOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (!checked) {
      onNonApplicantNameChange(null);
    } else {
      onNonApplicantNameChange('');
    }
    setShowOtherTextField(checked);
  };

  const handleOtherTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onNonApplicantNameChange(value);
  };

  return (
    <div className="flex flex-col gap-4">
      <TextField placeholder="Search by name" onChange={onSearchChange} />

      <div className="flex flex-col gap-3 sm:max-h-[226px] overflow-y-auto">
        {map(sortedSelectedApplicants, (applicant) => (
          <div key={`selected-${applicant.id}`} className="flex-none">
            <Checkbox
              id={applicant.id}
              checked
              onChange={onSelectionChange}
              value={applicant.id}
              text={
                <div className="flex items-center gap-2.5">
                  <Avatar
                    className="w-8 h-8"
                    name={applicant.fullName()}
                    variant="circle"
                    imageProps={{
                      src: applicant.avatar?.smallUrl || applicant.avatar?.downloadUrl,
                      alt: applicant.fullName(),
                    }}
                    backgroundClass="bg-primary-sunshine-yellow"
                  >
                    <UserGraphic className="w-full h-full flex-none" />
                  </Avatar>
                  <span className="text-sm font-bold text-neutral-ink-blue whitespace-normal">
                    {applicant.fullName()}
                  </span>
                </div>
              }
            />
          </div>
        ))}

        {loading ? (
          <div className="flex flex-col gap-3" data-testid="loading-skeleton">
            <div className="w-full flex items-center gap-3 py-2">
              <Skeleton className="h-4 w-4 flex-none rounded-sm" />
              <Skeleton className="h-3 w-64 rounded-full flex-none" />
            </div>
            <div className="w-full flex items-center gap-3 py-2">
              <Skeleton className="h-4 w-4 flex-none rounded-sm" />
              <Skeleton className="h-3 w-48 rounded-full flex-none" />
            </div>
            <div className="w-full flex items-center gap-3 py-2">
              <Skeleton className="h-4 w-4 flex-none rounded-sm" />
              <Skeleton className="h-3 w-56 rounded-full flex-none" />
            </div>
          </div>
        ) : (
          <>
            {!sortedApplicants.length ? (
              <p className="text-sm text-neutral-darker">No applicants found</p>
            ) : (
              map(sortedApplicants, (applicant) => (
                <div key={applicant.id} className="flex-none">
                  <Checkbox
                    id={applicant.id}
                    onChange={onSelectionChange}
                    value={applicant.id}
                    text={
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          className="w-8 h-8"
                          name={applicant.fullName()}
                          variant="circle"
                          imageProps={{
                            src: applicant.avatar?.smallUrl || applicant.avatar?.downloadUrl,
                            alt: applicant.fullName(),
                          }}
                          backgroundClass="bg-primary-sunshine-yellow"
                        >
                          <UserGraphic className="w-full h-full flex-none" />
                        </Avatar>
                        <span className="text-sm font-bold text-neutral-ink-blue whitespace-normal">
                          {applicant.fullName()}
                        </span>
                      </div>
                    }
                  />
                </div>
              ))
            )}
          </>
        )}
      </div>

      <div className="flex-none flex flex-col gap-2">
        <Checkbox
          id="other"
          checked={showOtherTextField}
          value="Other"
          onChange={handleOtherChange}
          text={<span className="font-bold text-neutral-ink-blue">Other</span>}
        />

        {(showOtherTextField || null) && (
          <TextField
            placeholder="Type the name here"
            value={nonApplicantName}
            onChange={handleOtherTextFieldChange}
          />
        )}
      </div>
    </div>
  );
};

SelectHiredPeople.displayName = 'SelectHiredPeople';
